import { PaytrieAPI } from '@paytrie/client'

import { CORPORATE_ENROLLMENT_QUERY_KEY } from '@/constants/corporateQueryKeys'
import { queryClient } from '@/utils/queryClient'

type CorporationAddress = NonNullable<PaytrieAPI.CorporationAddress>

type Upload = {
  objectName: string
  uploadUrl: string
  requiredHeaders: Record<string, string>
  expiresAt: string
}

type PersonInput = {
  firstName: string
  lastName: string
  email: string
  occupation: string
  dateOfBirth: string
}

type CorporateUpdate = Parameters<typeof PaytrieAPI.updateCurrentCorporation>[0]

export class RequestError extends Error {
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message)
  }
}

const assertOk = <T extends { status: number; data: unknown }>(
  res: T,
  action: string
): T => {
  if (res.status >= 200 && res.status < 300) return res
  const body = res.data as { errors?: { message?: string }[] } | undefined
  const message = body?.errors?.[0]?.message ?? `Failed to ${action}`
  throw new RequestError(message, res.status)
}

export const toPeople = (people: PersonInput[]) =>
  people.map((person) => ({
    firstName: person.firstName.trim(),
    lastName: person.lastName.trim(),
    email: person.email.trim().toLowerCase(),
    occupation: person.occupation.trim().toLowerCase(),
    dateOfBirth: new Date(person.dateOfBirth).toISOString().slice(0, 10),
  }))

export const toAddress = ({
  address1,
  address2,
  city,
  province,
  postal,
  country,
}: {
  address1: string
  address2?: string | null
  city: string
  province: string
  postal: string
  country?: string | null
}): CorporationAddress => ({
  addressLine1: address1,
  addressLine2: address2 ?? null,
  city,
  province: province.toLowerCase(),
  postalCode: postal.replace(/\s/g, '').toLowerCase(),
  country: (country ?? 'CA').toUpperCase() as CorporationAddress['country'],
})

const cacheEnrollment = <T extends { data: unknown }>(res: T): T => {
  const body = res.data as { data?: unknown } | undefined
  if (body?.data) {
    queryClient.setQueryData(CORPORATE_ENROLLMENT_QUERY_KEY, body.data)
  }
  return res
}

export const toRegionValue = ({
  country,
  province,
}: {
  country: string
  province: string
}) => (country.toUpperCase() === 'CA' ? province.toUpperCase() : province)

export const toPostalValue = ({
  country,
  postalCode,
}: {
  country: string
  postalCode: string
}) => {
  if (country.toUpperCase() !== 'CA') return postalCode
  const cleaned = postalCode.replace(/\s/g, '').toUpperCase()
  return cleaned.length > 3
    ? `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`
    : cleaned
}

export const getCorporate = () => PaytrieAPI.getCurrentCorporation()

export const startCorporateEnrollment = async (input: {
  corporationName: string
  corporationNumber: string
  corporationEmail: string
}) => {
  const existing = await getCorporate()
  if (existing.status === 200) return cacheEnrollment(existing)

  return cacheEnrollment(
    assertOk(
      await PaytrieAPI.createCorporation(input),
      'start your corporate enrollment'
    )
  )
}

export const updateCorporate = async (body: CorporateUpdate) =>
  cacheEnrollment(
    assertOk(
      await PaytrieAPI.updateCurrentCorporation(body),
      'save your corporate details'
    )
  )

type UploadableDocument = {
  file: File
  name: string
  contentType: string
}

export const uploadCorporateDocuments = async (
  documents: UploadableDocument[]
): Promise<string[]> => {
  const issued = assertOk(
    await PaytrieAPI.createCorporationDocumentUploadUrls({
      documents: documents.map(({ name, contentType, file }) => ({
        name,
        contentType:
          contentType as PaytrieAPI.CreateCorporationDocumentUploadUrlsBodyDocumentsItemContentType,
        size: file.size,
      })),
    }),
    'prepare your document upload'
  )

  const issuedBody = issued.data
  if (!('data' in issuedBody) || !('uploads' in issuedBody.data)) {
    throw new RequestError(
      'We could not prepare your document upload. Please try again.',
      issued.status
    )
  }
  const { uploads } = issuedBody.data

  await Promise.all(
    uploads.map(async (upload, index) => {
      const response = await fetch(upload.uploadUrl, {
        method: 'PUT',
        headers: upload.requiredHeaders,
        body: documents[index].file,
      })
      if (!response.ok) {
        throw new RequestError(
          'Your document could not be uploaded. Please try again.',
          response.status
        )
      }
    })
  )

  return uploads.map((upload) => upload.objectName)
}

export const addAdditionalCorporateDocuments = async (
  objectNames: string[]
) => {
  assertOk(
    await PaytrieAPI.addCorporationAdditionalDocuments({ objectNames }),
    'send your documents'
  )
}

export const submitCorporateDocuments = async (input: {
  objectNames: string[]
  incorporationJurisdiction: string
}) =>
  cacheEnrollment(
    assertOk(
      await PaytrieAPI.submitCorporationDocuments(
        input as Parameters<typeof PaytrieAPI.submitCorporationDocuments>[0]
      ),
      'submit your documents'
    )
  )
